<template>
    <b-row>
        <slot name="add">
            <b-col lg="12" class="mb-1">
                <div class="d-flex flex-row justify-content-center align-items-center" style="height: 100%;">
                    <b-card-text class="text-center">
                        <b-container>
                            <b-button v-ripple.400="'rgba(40, 199, 111, 0.15)'" variant="flat-primary" to="/tableau_bord/add">
                                <feather-icon class="text-primary mr-50 align-middle" icon="PlusSquareIcon" size="30"/>
                                <span class="text-primary align-middle">Ajouter Tableau de Bord</span>
                            </b-button>
                        </b-container>
                    </b-card-text>
                </div>
            </b-col>
        </slot>

        <b-col lg="12">
            <b-card>
                <!-- table -->
                <vue-good-table
                    :columns="columns"
                    :rows="tableauxBord"
                    :pagination-options="{enabled: true, perPage:pageLength}"
                    :initialSortBy="initialSortBy"
                    :theme="isDark ? 'nocturnal' : ''"
                    styleClass="vgt-table bordered tableau"
                    >

                >
                    <template #emptystate>
                        <div class="text-center"> Aucun Tableau de Bord n'a été trouvé </div>
                    </template>

                    <template slot="table-row" slot-scope="props">
                        <span v-if="props.column.field === 'action'" class="text-nowrap">
                            <b-button
                                size="sm"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="success"
                                class="btn-icon"
                                @click="editTableauBord(props.row.id)"
                            >
                                <feather-icon icon="Edit2Icon" size="1x"/>
                            </b-button>

                            <b-button
                                size="sm"
                                v-if="deletable(props.row)"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                variant="danger"
                                class="btn-icon ml-1"
                                @click="confirmDeleteTableauBord(props.row.id, 'du ' + moment(props.row.date, 'YYYY-MM-DD').format('DD/MM/YYYY'), removeTableauBordFromList)"
                            >
                                <feather-icon icon="TrashIcon" size="1x"/>
                            </b-button>
                        </span>

                        <template v-else-if="props.column.field === 'valid'" class="text-nowrap">
                            <div class="text-center text-primary"><feather-icon icon="CheckCircleIcon" size="18" v-if="props.row.valid" /></div>
                        </template>

                        <template v-else-if="props.column.field === 'date'" class="text-nowrap">
                            {{moment(props.row.date, 'YYYY-MM-DD').format('DD/MM/YYYY')}}
                        </template>
                    </template>

                    <template
                        slot="pagination-bottom"
                        slot-scope="props"
                    >
                        <div class="d-flex justify-content-between flex-wrap">
                        <div class="d-flex align-items-center mb-0 mt-1">
                            <span class="text-nowrap">
                            </span>
                            <b-form-select
                                v-model="pageLength"
                                :options="['25', '50', '100']"
                                class="mx-1"
                                @input="(value)=>props.perPageChanged({currentPerPage:value})"
                            />
                            <span class="text-nowrap "> sur {{ props.total }} Tableaux </span>
                        </div>
                        <div>
                            <b-pagination
                            :value="1"
                            :total-rows="props.total"
                            :per-page="pageLength"
                            first-number
                            last-number
                            align="right"
                            prev-class="prev-item"
                            next-class="next-item"
                            class="mt-1 mb-0"
                            @input="(value)=>props.pageChanged({currentPage:value})"
                            >
                            <template #prev-text>
                                <feather-icon
                                icon="ChevronLeftIcon"
                                size="18"
                                />
                            </template>
                            <template #next-text>
                                <feather-icon
                                icon="ChevronRightIcon"
                                size="18"
                                />
                            </template>
                            </b-pagination>
                        </div>
                        </div>
                    </template>
                </vue-good-table>
            </b-card>
        </b-col>
    </b-row>
</template>

<script>
    import 'vue-good-table/dist/vue-good-table.css'
    import { VueGoodTable } from 'vue-good-table'

    import tableauBordApi from "@/api/tableauBordApi"
    import moment from "moment"
    import { formatNumber, itemMatch } from "@/services/utils"
    import { confirmDeleteTableauBord, deleteTableauBord } from "@/views/setups/tableauBord"

    import useAppConfig from '@core/app-config/useAppConfig'
    import { computed } from '@vue/composition-api'
    import { mapState, mapGetters, mapMutations } from "vuex"

    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

    export default {
        props: {
            context: {default: () => {}}
        },

        components: {
            VueGoodTable,
            ToastificationContent
        },

        data() {
            return {
                tableauxBord: [],
                initialSortBy: [{field: 'nom', type: 'asc'}],
                pageLength: 25,
                columns: [
                    {
                        label: 'Date',
                        field: 'date',
                        filterOptions: {enabled: false, placeholder: 'Date Point Bord ...', customFilter: true},
                    },
                    {
                        label: 'Validé',
                        field: 'valid',
                    },
                    {
                        label: 'Action',
                        field: 'action',
                        width: '10%',
                        sortable: false
                    },
                ],
            }
        },

        beforeMount() {
            this.refreshTableauxBord();
        },

        setup() {
            const { skin } = useAppConfig()
            const isDark = computed(() => skin.value === 'dark')

            return { skin, isDark, deleteTableauBord, confirmDeleteTableauBord, formatNumber, moment}
        },

        methods: {
            async refreshTableauxBord() {
                /* console.log(process.env.NODE_ENV == "development") */
                this.tableauxBord = await tableauBordApi.getTableauxBord();
            },

            editTableauBord(tableauBordId) {
                this.$router.push({path: `/tableau_bord/${tableauBordId}/edit` })
            },

            showToast(variant, text, icon) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        icon,
                        text,
                        variant
                    },
                }, {position: "bottom-right", timeout: 2500})
            },

            deletable(tableauBord) {
                return !tableauBord.valid
            },

            removeTableauBordFromList(tableauBordId) {
                let index = this.tableauxBord.findIndex(t => t.id == tableauBordId)
                if(index != -1)
                    this.tableauxBord.splice(index, 1)
            }
        }
    }
</script>

<style>
    .dropdown > button{
        padding: 0 !important;
    }
</style>